import React, { memo } from "react";
import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import { Box } from "@/components/atoms/Box";
import AppleStyleSwipeableRow from "@/components/molecules/AppleStyleSwipeableRow";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import InfoPlayer from "@/components/organisms/InfoPlayer";
import { IInfoBadge } from "@/components/organisms/InfoPlayer/InfoPlayerView";
import { CurrencyCode } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Props for PlayerItemView component
 */
interface Props {
  item: PlayerCardData;
  onDelete: (() => Promise<unknown> | null) | null;
  loading: boolean;
  isActive: boolean;
  drag?: () => void;
  onSearch: () => void;
  onAdd: () => void;
  showActionsheet: boolean;
  onClose: () => void;
  onAddWatchlistPress: () => void;
  isPlayerInAnyWatchlist: boolean;
  infoBadge?: IInfoBadge;
  onPress?: () => void;
  onPressViewItem?: () => void;
  playerName: string;
  onCompare: () => void;
  currencyCode: CurrencyCode;
  isSmallScreen: boolean;
  isDark: boolean;
  promptRef?: React.RefObject<CustomPromptRefProps>;
}

/**
 * Pure view component for player item
 * @param root0 - Component props
 * @param root0.item - Player data
 * @param root0.loading - Whether data is loading
 * @param root0.onDelete - Handler for delete action
 * @param root0.isActive - Whether item is active
 * @param root0.drag - Handler for drag gesture
 * @param root0.onSearch - Handler for search action
 * @param root0.onAdd - Handler for add action
 * @param root0.showActionsheet - Whether actionsheet is shown
 * @param root0.onClose - Handler for close action
 * @param root0.onAddWatchlistPress - Handler for add to watchlist
 * @param root0.isPlayerInAnyWatchlist - Whether player is in any watchlist
 * @param root0.infoBadge - Badge configuration
 * @param root0.onPress - Handler for press action
 * @param root0.playerName - Player display name
 * @param root0.onCompare - Handler for compare action
 * @param root0.promptRef - Reference to custom prompt
 * @param root0.currencyCode - Currency code for formatting
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.isDark - Whether dark theme is active
 * @param root0.onPressViewItem - Handler for view item press
 */
const PlayerItemView = ({
  item,
  loading,
  onDelete,
  isActive,
  drag,
  onSearch,
  onAdd,
  showActionsheet,
  onClose,
  onAddWatchlistPress,
  isPlayerInAnyWatchlist,
  infoBadge,
  onPress,
  playerName,
  onCompare,
  promptRef,
  currencyCode,
  isSmallScreen,
  isDark,
  onPressViewItem,
}: Props) =>
  !isSmallScreen ? (
    <>
      <Box className="min-h-20 grow justify-center py-2">
        <InfoPlayer
          onPress={onPressViewItem}
          drag={drag}
          onAddPress={onAdd}
          loading={loading}
          isActive={isActive}
          playerName={playerName}
          item={item}
          infoBadge={infoBadge}
          currencyCode={currencyCode}
          isPlayerInAnyWatchList={isPlayerInAnyWatchlist}
          isSmallScreen={isSmallScreen}
          isDark={isDark}
        />
      </Box>
      {showActionsheet && (
        <AddToWatchlistActionsheet
          item={item}
          showActionsheet={showActionsheet}
          onClose={onClose}
          onAddPress={onAdd}
          onAddWatchlistPress={onAddWatchlistPress}
          loading={loading}
          isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
          promptRef={promptRef}
          isDark={isDark}
        />
      )}
    </>
  ) : (
    <AppleStyleSwipeableRow
      onDelete={isActive || loading ? undefined : onDelete}
      onSearch={isActive || loading ? undefined : onSearch}
      onAdd={isActive || loading ? undefined : onAdd}
      onCompare={isActive || loading ? undefined : onCompare}
      isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
    >
      <Box className="min-h-20 grow justify-center py-2">
        <InfoPlayer
          onPress={onPress}
          drag={drag}
          loading={loading}
          isActive={isActive}
          playerName={playerName}
          item={item}
          infoBadge={infoBadge}
          currencyCode={currencyCode}
          isSmallScreen={isSmallScreen}
          isDark={isDark}
        />
        {showActionsheet && (
          <AddToWatchlistActionsheet
            item={item}
            showActionsheet={showActionsheet}
            onClose={onClose}
            onAddPress={onAdd}
            onAddWatchlistPress={onAddWatchlistPress}
            loading={loading}
            isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
            promptRef={promptRef}
            isDark={isDark}
          />
        )}
      </Box>
    </AppleStyleSwipeableRow>
  );

PlayerItemView.displayName = "PlayerItemView";

export default memo(PlayerItemView);
